import axios from 'axios'

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1']
if (process.env.REACT_APP_LOCAL_IP) {
  LOCAL_HOSTNAMES.push(process.env.REACT_APP_LOCAL_IP)
}

const isLocalhost = LOCAL_HOSTNAMES.includes(window.location.hostname)

const API_URL =
  process.env.REACT_APP_API_URL ||
  (isLocalhost
    ? 'http://localhost:8090/api'
    : process.env.REACT_APP_API_URL_PROD ||
      'https://tribulum.redirectme.net/api')

// ── Token en memoria del módulo + sessionStorage ──────────────────────────────
// sessionStorage persiste a través de recargas del mismo tab (F5) pero no entre
// tabs ni ventanas. Es el punto medio óptimo: más persistente que solo-memoria,
// más seguro que localStorage (no compartido entre tabs).
// En entornos cross-site (rustaro.com → tribulum), las cookies SameSite=None
// son bloqueadas por Chrome 120+ / Safari ITP, por lo que el token en
// sessionStorage es el único mecanismo fiable de persistencia en recarga.
const _SESSION_KEY = 'rustaro_at'

let _accessToken = sessionStorage.getItem(_SESSION_KEY) || null

export const setAccessToken = (token) => {
  _accessToken = token
  if (token) sessionStorage.setItem(_SESSION_KEY, token)
  else sessionStorage.removeItem(_SESSION_KEY)
}

export const clearAccessToken = () => {
  _accessToken = null
  sessionStorage.removeItem(_SESSION_KEY)
}

export const getAccessToken = () => _accessToken

// ── Cliente axios ─────────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true, // envía cookies httpOnly como capa adicional cuando el navegador las admita
  headers: {
    'Content-Type': 'application/json',
  },
})

let refreshPromise = null

const refreshAccessToken = async () => {
  // withCredentials envía la cookie refresh_token si el navegador la admite.
  // El servidor devuelve el nuevo access token también en el cuerpo (mecanismo dual)
  // para entornos cross-site donde las cookies de terceros están bloqueadas.
  const response = await apiClient.post('/auth/refresh')
  const { accessToken } = response.data
  if (accessToken) setAccessToken(accessToken)
  return response.data
}

// ── Interceptor de petición: añade cabecera Authorization ─────────────────────
apiClient.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`
  }
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[API] -> ${config.method?.toUpperCase()} ${config.url}`, {
      data: config.data,
    })
  }
  return config
})

// ── Interceptor de respuesta: reintento con refresh en 401 ────────────────────
apiClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        `[API] <- ${response.config.method?.toUpperCase()} ${response.config.url}`,
        { status: response.status },
      )
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (process.env.NODE_ENV !== 'production') {
      console.error(
        `[API] Error en ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}:`,
        { status: error.response?.status },
      )
    }

    // Reintentar una vez con refresh token si recibimos 401 (no en rutas de auth)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/')
    ) {
      originalRequest._retry = true
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null
          })
        }
        await refreshPromise
        // El interceptor de request añadirá la nueva cabecera Authorization en el reintento
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('[API] Error al refrescar token:', refreshError)
        clearAccessToken()
        // Notificar a AuthContext para que limpie el estado y deje que
        // React Router navegue a /login (evita el hard-reload con window.location)
        window.dispatchEvent(new CustomEvent('auth:session-expired'))
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

// Exportaciones de compatibilidad (sin efecto, evitan romper importaciones existentes)
export const setRememberCache = () => {}
export const invalidateTokenCache = () => {}

export default apiClient
